/**
 * CalendarSettingsTab.jsx
 * Il Diario di Bordo - SaaS
 * 
 * Tab per impostazioni calendario chiusure in SettingsPage
 * Include: orari, giorni apertura, festività, patrono, chiusure, aperture, export iCal
 */

import { useState, useEffect } from 'react'
import { calendarioApi } from '../../services/api'
import { Card, Toggle } from '../ui'
import { 
  Calendar, 
  Clock, 
  ChevronDown, 
  ChevronUp, 
  Plus, 
  X, 
  Trash2,
  Download,
  Copy,
  Check,
  Loader2,
  AlertTriangle
} from 'lucide-react'

// Festività italiane fisse
const FESTIVITA_ITALIANE = [
  { codice: 'CAPODANNO', nome: 'Capodanno', giorno: 1, mese: 1, emoji: '🎆' },
  { codice: 'EPIFANIA', nome: 'Epifania', giorno: 6, mese: 1, emoji: '👑' },
  { codice: 'PASQUA', nome: 'Pasqua', emoji: '🐣', mobile: true },
  { codice: 'PASQUETTA', nome: 'Lunedì dell\'Angelo', emoji: '🐰', mobile: true },
  { codice: 'LIBERAZIONE', nome: 'Festa della Liberazione', giorno: 25, mese: 4, emoji: '🇮🇹' },
  { codice: 'LAVORO', nome: 'Festa dei Lavoratori', giorno: 1, mese: 5, emoji: '⚒️' },
  { codice: 'REPUBBLICA', nome: 'Festa della Repubblica', giorno: 2, mese: 6, emoji: '🇮🇹' },
  { codice: 'FERRAGOSTO', nome: 'Ferragosto', giorno: 15, mese: 8, emoji: '☀️' },
  { codice: 'SAN_FRANCESCO', nome: 'San Francesco (Patrono d\'Italia)', giorno: 4, mese: 10, emoji: '🕊️' },
  { codice: 'OGNISSANTI', nome: 'Tutti i Santi', giorno: 1, mese: 11, emoji: '👼' },
  { codice: 'IMMACOLATA', nome: 'Immacolata Concezione', giorno: 8, mese: 12, emoji: '🙏' },
  { codice: 'NATALE', nome: 'Natale', giorno: 25, mese: 12, emoji: '🎄' },
  { codice: 'SANTO_STEFANO', nome: 'Santo Stefano', giorno: 26, mese: 12, emoji: '🎁' }
]

const GIORNI_SETTIMANA = [
  { id: 'lunedi', label: 'Lun' },
  { id: 'martedi', label: 'Mar' },
  { id: 'mercoledi', label: 'Mer' },
  { id: 'giovedi', label: 'Gio' },
  { id: 'venerdi', label: 'Ven' },
  { id: 'sabato', label: 'Sab' },
  { id: 'domenica', label: 'Dom' }
]

const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
]

export default function CalendarSettingsTab({ settings, setSettings, showSuccess, showError }) {
  const [expandedSections, setExpandedSections] = useState(['orari', 'giorni'])
  const [showChiusuraModal, setShowChiusuraModal] = useState(false)
  const [showAperturaModal, setShowAperturaModal] = useState(false)
  const [icalToken, setIcalToken] = useState(null)
  const [loadingIcal, setLoadingIcal] = useState(false)
  const [copied, setCopied] = useState(false)
  const [saving, setSaving] = useState(false)
  
  // Form chiusura
  const [chiusuraForm, setChiusuraForm] = useState({
    dataInizio: '',
    dataFine: '',
    motivo: '',
    tipologia: 'intera',
    ricorrente: false
  })
  
  // Form apertura
  const [aperturaForm, setAperturaForm] = useState({
    data: '',
    motivo: '',
    orarioApertura: '',
    orarioChiusura: ''
  })
  
  // Form santo patrono (stato locale per evitare chiamate API ad ogni keystroke)
  const [patronoForm, setPatronoForm] = useState({
    nome: '',
    giorno: '',
    mese: ''
  })
  
  // Flag per tracciare se il patrono form è stato modificato
  const [patronoModified, setPatronoModified] = useState(false)
  
  // Stato locale per orari (salvataggio solo su onBlur)
  const [orariLocali, setOrariLocali] = useState({ apertura: '', chiusura: '' })
  
  const calendario = settings?.calendario || {}
  
  // Sincronizza patronoForm quando cambiano i settings (es. al caricamento iniziale)
  useEffect(() => {
    setPatronoForm({
      nome: calendario.santoPatrono?.nome || '',
      giorno: calendario.santoPatrono?.giorno ? String(calendario.santoPatrono.giorno) : '',
      mese: calendario.santoPatrono?.mese ? String(calendario.santoPatrono.mese) : ''
    })
    setPatronoModified(false)
  }, [calendario.santoPatrono?.nome, calendario.santoPatrono?.giorno, calendario.santoPatrono?.mese])
  
  // Sincronizza orariLocali quando cambiano i settings (caricamento iniziale)
  useEffect(() => {
    setOrariLocali({
      apertura: calendario.orarioApertura || '08:00',
      chiusura: calendario.orarioChiusura || '18:00'
    })
  }, [calendario.orarioApertura, calendario.orarioChiusura])
  
  // Toggle sezione espansa
  const toggleSection = (section) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    )
  }
  
  // Aggiorna impostazioni calendario
  const updateCalendario = async (updates) => {
    setSaving(true)
    try {
      await calendarioApi.updateImpostazioni(updates)
      setSettings(prev => ({
        ...prev,
        calendario: { ...prev?.calendario, ...updates }
      }))
      showSuccess?.('Impostazioni salvate')
    } catch (error) {
      showError?.(error.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }
  
  // Toggle giorno apertura
  const toggleGiornoApertura = async (giorno) => {
    const giorniApertura = calendario.giorniApertura || ['lunedi', 'martedi', 'mercoledi', 'giovedi', 'venerdi']
    const newGiorni = giorniApertura.includes(giorno)
      ? giorniApertura.filter(g => g !== giorno)
      : [...giorniApertura, giorno]
    
    await updateCalendario({ giorniApertura: newGiorni })
  }
  
  // Toggle festività enabled - FIX: usa struttura corretta
  const toggleFestivitaEnabled = async (enabled) => {
    setSaving(true)
    try {
      await calendarioApi.updateImpostazioni({ 
        festivitaItaliane: { 
          ...calendario.festivitaItaliane,
          enabled 
        } 
      })
      setSettings(prev => ({
        ...prev,
        calendario: {
          ...prev?.calendario,
          festivitaItaliane: {
            ...prev?.calendario?.festivitaItaliane,
            enabled
          }
        }
      }))
      showSuccess?.('Impostazioni salvate')
    } catch (error) {
      showError?.(error.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }
  
  // Toggle festività lavorativa
  const toggleFestivitaLavorativa = async (codice) => {
    try {
      const lavorative = calendario.festivitaItaliane?.lavorative || []
      const isLavorativa = lavorative.some(l => l.codice === codice)
      
      await calendarioApi.updateFestivita(codice, { lavorativa: !isLavorativa, anno: null })
      
      const newLavorative = isLavorativa
        ? lavorative.filter(l => l.codice !== codice)
        : [...lavorative, { codice, anno: null }]
      
      setSettings(prev => ({
        ...prev,
        calendario: {
          ...prev?.calendario,
          festivitaItaliane: {
            ...prev?.calendario?.festivitaItaliane,
            lavorative: newLavorative
          }
        }
      }))
      
      showSuccess?.('Festività aggiornata')
    } catch (error) {
      showError?.(error.response?.data?.message || 'Errore')
    }
  }
  
  // Aggiungi chiusura
  const handleAddChiusura = async () => {
    if (!chiusuraForm.dataInizio || !chiusuraForm.motivo) {
      showError?.('Compila data e motivo')
      return
    }
    
    try {
      await calendarioApi.addChiusura(chiusuraForm)
      setSettings(prev => ({
        ...prev,
        calendario: {
          ...prev?.calendario,
          chiusure: [...(prev?.calendario?.chiusure || []), chiusuraForm]
        }
      }))
      setShowChiusuraModal(false)
      setChiusuraForm({ dataInizio: '', dataFine: '', motivo: '', tipologia: 'intera', ricorrente: false })
      showSuccess?.('Chiusura aggiunta')
    } catch (error) {
      showError?.(error.response?.data?.message || 'Errore')
    }
  }
  
  // Rimuovi chiusura
  const handleRemoveChiusura = async (id) => {
    try {
      await calendarioApi.removeChiusura(id)
      setSettings(prev => ({
        ...prev,
        calendario: {
          ...prev?.calendario,
          chiusure: prev?.calendario?.chiusure?.filter(c => c._id !== id) || []
        }
      }))
      showSuccess?.('Chiusura rimossa')
    } catch (error) {
      showError?.(error.response?.data?.message || 'Errore')
    }
  }
  
  // Aggiungi apertura straordinaria
  const handleAddApertura = async () => {
    if (!aperturaForm.data) {
      showError?.('Seleziona una data')
      return
    }
    
    try {
      await calendarioApi.addAperturaEccezione(aperturaForm)
      setSettings(prev => ({
        ...prev,
        calendario: {
          ...prev?.calendario,
          apertureEccezioni: [...(prev?.calendario?.apertureEccezioni || []), aperturaForm]
        }
      }))
      setShowAperturaModal(false)
      setAperturaForm({ data: '', motivo: '', orarioApertura: '', orarioChiusura: '' })
      showSuccess?.('Apertura aggiunta')
    } catch (error) {
      showError?.(error.response?.data?.message || 'Errore')
    }
  }
  
  // Rimuovi apertura
  const handleRemoveApertura = async (id) => {
    try {
      await calendarioApi.removeAperturaEccezione(id)
      setSettings(prev => ({
        ...prev,
        calendario: {
          ...prev?.calendario,
          apertureEccezioni: prev?.calendario?.apertureEccezioni?.filter(a => a._id !== id) || []
        }
      }))
      showSuccess?.('Apertura rimossa')
    } catch (error) {
      showError?.(error.response?.data?.message || 'Errore')
    }
  }
  
  // Carica token iCal
  const loadIcalToken = async () => {
    setLoadingIcal(true)
    try {
      const res = await calendarioApi.getIcalToken()
      setIcalToken(res.data?.data)
    } catch (error) {
      console.error('Errore caricamento token iCal:', error)
    } finally {
      setLoadingIcal(false)
    }
  }
  
  // Copia link iCal
  const copyIcalLink = () => {
    if (icalToken?.url) {
      navigator.clipboard.writeText(icalToken.url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }
  
  // Aggiorna santo patrono - silent=true per non mostrare messaggio
  const updateSantoPatrono = async (updates, { silent = false } = {}) => {
    try {
      const newPatrono = { ...calendario.santoPatrono, ...updates }
      await calendarioApi.updateSantoPatrono(newPatrono)
      setSettings(prev => ({
        ...prev,
        calendario: {
          ...prev?.calendario,
          santoPatrono: newPatrono
        }
      }))
      if (!silent) {
        showSuccess?.('Santo Patrono aggiornato')
      }
      setPatronoModified(false)
    } catch (error) {
      showError?.(error.response?.data?.message || 'Errore')
    }
  }
  
  // Salva patrono form su blur (solo se modificato)
  const savePatronoOnBlur = async () => {
    if (!patronoModified) return
    
    const updates = {}
    
    // Confronta nome (stringa)
    if (patronoForm.nome !== (calendario.santoPatrono?.nome || '')) {
      updates.nome = patronoForm.nome
    }
    
    // Confronta giorno (converto entrambi a stringa per confronto sicuro)
    const currentGiorno = String(calendario.santoPatrono?.giorno || '')
    const newGiorno = String(patronoForm.giorno || '')
    if (newGiorno && newGiorno !== currentGiorno) {
      updates.giorno = parseInt(patronoForm.giorno)
    }
    
    // Confronta mese (converto entrambi a stringa per confronto sicuro)
    const currentMese = String(calendario.santoPatrono?.mese || '')
    const newMese = String(patronoForm.mese || '')
    if (newMese && newMese !== currentMese) {
      updates.mese = parseInt(patronoForm.mese)
    }
    
    if (Object.keys(updates).length > 0) {
      await updateSantoPatrono(updates)
    } else {
      setPatronoModified(false)
    }
  }
  
  const SectionHeader = ({ id, title, icon: Icon, expanded }) => (
    <button
      onClick={() => toggleSection(id)}
      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
    >
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-primary-600" />
        <span className="font-semibold text-gray-900 dark:text-white">{title}</span>
      </div>
      {expanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
    </button>
  )
  
  return (
    <div className="space-y-4">
      {/* Orari Giornalieri */}
      <Card padding={false}>
        <SectionHeader id="orari" title="Orari Giornalieri" icon={Clock} expanded={expandedSections.includes('orari')} />
        {expandedSections.includes('orari') && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Apertura</label>
              <input
                type="time"
                value={orariLocali.apertura}
                onChange={(e) => setOrariLocali(prev => ({ ...prev, apertura: e.target.value }))}
                onBlur={(e) => { if (e.target.value) updateCalendario({ orarioApertura: e.target.value }) }}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Chiusura</label>
              <input
                type="time"
                value={orariLocali.chiusura}
                onChange={(e) => setOrariLocali(prev => ({ ...prev, chiusura: e.target.value }))}
                onBlur={(e) => { if (e.target.value) updateCalendario({ orarioChiusura: e.target.value }) }}
                className="input"
              />
            </div>
          </div>
        )}
      </Card>

      {/* Giorni Apertura */}
      <Card padding={false}>
        <SectionHeader id="giorni" title="Giorni di Apertura" icon={Calendar} expanded={expandedSections.includes('giorni')} />
        {expandedSections.includes('giorni') && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-700">
            <div className="flex gap-2 flex-wrap">
              {GIORNI_SETTIMANA.map(giorno => {
                const isOpen = (calendario.giorniApertura || ['lunedi', 'martedi', 'mercoledi', 'giovedi', 'venerdi']).includes(giorno.id)
                return (
                  <button
                    key={giorno.id}
                    onClick={() => toggleGiornoApertura(giorno.id)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      isOpen
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                  >
                    {giorno.label}
                  </button>
                )
              })}
            </div>
            <p className="mt-3 text-xs text-gray-500">Verde = aperto, Grigio = chiuso</p>
          </div>
        )}
      </Card>

      {/* Festività Italiane */}
      <Card padding={false}>
        <SectionHeader id="festivita" title="Festività Italiane" icon={Calendar} expanded={expandedSections.includes('festivita')} />
        {expandedSections.includes('festivita') && (
          <div className="border-t border-gray-100 dark:border-gray-700">
            <div className="p-4">
              <label className="flex items-center gap-3">
                <Toggle
                  checked={calendario.festivitaItaliane?.enabled !== false}
                  onChange={(checked) => toggleFestivitaEnabled(checked)}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Chiudi automaticamente per festività italiane</span>
              </label>
            </div>
            {calendario.festivitaItaliane?.enabled !== false && (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                <div className="px-4 py-2 bg-purple-50 dark:bg-purple-900/20">
                  <p className="text-xs text-purple-700 dark:text-purple-300">
                    ⭐ Clicca su "Chiuso" per rendere una festività lavorativa (centro aperto)
                  </p>
                </div>
                {FESTIVITA_ITALIANE.map(f => {
                  const lavorative = calendario.festivitaItaliane?.lavorative || []
                  const isLavorativa = lavorative.some(l => l.codice === f.codice)
                  
                  return (
                    <div key={f.codice} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{f.emoji}</span>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{f.nome}</p>
                          <p className="text-xs text-gray-500">
                            {f.mobile ? 'Data variabile' : `${f.giorno}/${f.mese}`}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleFestivitaLavorativa(f.codice)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          isLavorativa
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                        }`}
                      >
                        {isLavorativa ? '⭐ Aperto' : '🚫 Chiuso'}
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Santo Patrono */}
      <Card padding={false}>
        <SectionHeader id="patrono" title="Santo Patrono" icon={Calendar} expanded={expandedSections.includes('patrono')} />
        {expandedSections.includes('patrono') && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-700 space-y-4">
            <label className="flex items-center gap-3">
              <Toggle
                checked={calendario.santoPatrono?.enabled || false}
                onChange={(checked) => updateSantoPatrono({ enabled: checked })}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Chiudi per Santo Patrono locale</span>
            </label>
            
            {calendario.santoPatrono?.enabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome</label>
                  <input
                    type="text"
                    value={patronoForm.nome}
                    onChange={(e) => {
                      setPatronoForm(prev => ({ ...prev, nome: e.target.value }))
                      setPatronoModified(true)
                    }}
                    onBlur={savePatronoOnBlur}
                    placeholder="es. San Giovanni"
                    className="input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Giorno</label>
                    <select
                      value={patronoForm.giorno}
                      onChange={(e) => {
                        setPatronoForm(prev => ({ ...prev, giorno: e.target.value }))
                        setPatronoModified(true)
                      }}
                      onBlur={savePatronoOnBlur}
                      className="input"
                    >
                      <option value="">--</option>
                      {[...Array(31)].map((_, i) => (
                        <option key={i+1} value={i+1}>{i+1}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mese</label>
                    <select
                      value={patronoForm.mese}
                      onChange={(e) => {
                        setPatronoForm(prev => ({ ...prev, mese: e.target.value }))
                        setPatronoModified(true)
                      }}
                      onBlur={savePatronoOnBlur}
                      className="input"
                    >
                      <option value="">--</option>
                      {MESI.map((m, i) => (
                        <option key={i+1} value={i+1}>{m}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <label className="flex items-center gap-3">
                  <Toggle
                    checked={calendario.santoPatrono?.lavorativo || false}
                    onChange={(checked) => updateSantoPatrono({ lavorativo: checked })}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Centro aperto nonostante la festività</span>
                </label>
              </>
            )}
          </div>
        )}
      </Card>

      {/* Chiusure Straordinarie */}
      <Card padding={false}>
        <SectionHeader id="chiusure" title="Chiusure Straordinarie" icon={AlertTriangle} expanded={expandedSections.includes('chiusure')} />
        {expandedSections.includes('chiusure') && (
          <div className="border-t border-gray-100 dark:border-gray-700">
            {(calendario.chiusure || []).length > 0 ? (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {calendario.chiusure.map((c, idx) => (
                  <div key={c._id || idx} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{c.motivo}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(c.dataInizio).toLocaleDateString('it-IT')}
                        {c.dataFine && ` - ${new Date(c.dataFine).toLocaleDateString('it-IT')}`}
                        {c.tipologia !== 'intera' && ` (solo ${c.tipologia})`}
                        {c.ricorrente && ' • 🔄 Ricorrente'}
                      </p>
                    </div>
                    <button onClick={() => handleRemoveChiusura(c._id)} className="text-red-500 hover:text-red-700 p-2">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="p-4 text-sm text-gray-500 text-center">Nessuna chiusura straordinaria programmata</p>
            )}
            <div className="p-4 border-t border-gray-100 dark:border-gray-700">
              <button onClick={() => setShowChiusuraModal(true)} className="btn-primary w-full">
                <Plus className="h-4 w-4 mr-2" />Aggiungi Chiusura
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Aperture Straordinarie */}
      <Card padding={false}>
        <SectionHeader id="aperture" title="Aperture Straordinarie" icon={Calendar} expanded={expandedSections.includes('aperture')} />
        {expandedSections.includes('aperture') && (
          <div className="border-t border-gray-100 dark:border-gray-700">
            <div className="px-4 py-2 bg-purple-50 dark:bg-purple-900/20">
              <p className="text-xs text-purple-700 dark:text-purple-300">
                ⭐ Le aperture straordinarie permettono di aprire in giorni normalmente chiusi (weekend, festivi)
              </p>
            </div>
            {(calendario.apertureEccezioni || []).length > 0 ? (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {calendario.apertureEccezioni.map((a, idx) => (
                  <div key={a._id || idx} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        ⭐ {a.motivo || 'Apertura straordinaria'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(a.data).toLocaleDateString('it-IT')}
                        {a.orarioApertura && ` • ${a.orarioApertura} - ${a.orarioChiusura}`}
                      </p>
                    </div>
                    <button onClick={() => handleRemoveApertura(a._id)} className="text-red-500 hover:text-red-700 p-2">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="p-4 text-sm text-gray-500 text-center">Nessuna apertura straordinaria programmata</p>
            )}
            <div className="p-4 border-t border-gray-100 dark:border-gray-700">
              <button onClick={() => setShowAperturaModal(true)} className="btn-secondary w-full">
                <Plus className="h-4 w-4 mr-2" />Aggiungi Apertura
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Export iCal */}
      <Card padding={false}>
        <SectionHeader id="ical" title="Export Calendario (iCal)" icon={Download} expanded={expandedSections.includes('ical')} />
        {expandedSections.includes('ical') && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-700 space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Abbona il calendario delle chiusure al tuo dispositivo per ricevere aggiornamenti automatici.
            </p>
            
            {!icalToken && (
              <button onClick={loadIcalToken} disabled={loadingIcal} className="btn-secondary">
                {loadingIcal ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Genera Link Abbonamento
              </button>
            )}
            
            {icalToken && (
              <>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={icalToken.url}
                    readOnly
                    className="input flex-1 text-xs bg-gray-50 dark:bg-gray-700"
                  />
                  <button onClick={copyIcalLink} className="btn-secondary">
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm">
                  <p className="font-medium text-blue-800 dark:text-blue-200 mb-2">Come usare:</p>
                  <ul className="text-blue-700 dark:text-blue-300 space-y-1 text-xs">
                    <li>• <strong>iPhone/iPad:</strong> Impostazioni → Calendario → Account → Aggiungi → Altro → Abbonamento calendario</li>
                    <li>• <strong>Android:</strong> Google Calendar → Impostazioni → Aggiungi calendario → Da URL</li>
                    <li>• <strong>Outlook:</strong> File → Impostazioni account → Calendari Internet → Nuovo</li>
                  </ul>
                </div>
                
                <a 
                  href={icalToken.url} 
                  download="chiusure.ics"
                  className="btn-secondary inline-flex items-center"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Scarica file .ics
                </a>
              </>
            )}
          </div>
        )}
      </Card>

      {/* Modal Aggiungi Chiusura */}
      {showChiusuraModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Nuova Chiusura</h3>
              <button onClick={() => setShowChiusuraModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data Inizio *</label>
                <input
                  type="date"
                  value={chiusuraForm.dataInizio}
                  onChange={(e) => setChiusuraForm(prev => ({ ...prev, dataInizio: e.target.value }))}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data Fine (opzionale)</label>
                <input
                  type="date"
                  value={chiusuraForm.dataFine}
                  onChange={(e) => setChiusuraForm(prev => ({ ...prev, dataFine: e.target.value }))}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Motivo *</label>
                <input
                  type="text"
                  value={chiusuraForm.motivo}
                  onChange={(e) => setChiusuraForm(prev => ({ ...prev, motivo: e.target.value }))}
                  placeholder="es. Vacanze estive"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo</label>
                <select
                  value={chiusuraForm.tipologia}
                  onChange={(e) => setChiusuraForm(prev => ({ ...prev, tipologia: e.target.value }))}
                  className="input"
                >
                  <option value="intera">Giornata intera</option>
                  <option value="mattina">Solo mattina</option>
                  <option value="pomeriggio">Solo pomeriggio</option>
                </select>
              </div>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={chiusuraForm.ricorrente}
                  onChange={(e) => setChiusuraForm(prev => ({ ...prev, ricorrente: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">🔄 Ricorrente ogni anno</span>
              </label>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t dark:border-gray-700">
              <button onClick={() => setShowChiusuraModal(false)} className="btn-secondary">Annulla</button>
              <button onClick={handleAddChiusura} className="btn-primary">Aggiungi</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Aggiungi Apertura */}
      {showAperturaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Nuova Apertura Straordinaria</h3>
              <button onClick={() => setShowAperturaModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data *</label>
                <input
                  type="date"
                  value={aperturaForm.data}
                  onChange={(e) => setAperturaForm(prev => ({ ...prev, data: e.target.value }))}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Motivo</label>
                <input
                  type="text"
                  value={aperturaForm.motivo}
                  onChange={(e) => setAperturaForm(prev => ({ ...prev, motivo: e.target.value }))}
                  placeholder="es. Evento speciale"
                  className="input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Orario Apertura</label>
                  <input
                    type="time"
                    value={aperturaForm.orarioApertura}
                    onChange={(e) => setAperturaForm(prev => ({ ...prev, orarioApertura: e.target.value }))}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Orario Chiusura</label>
                  <input
                    type="time"
                    value={aperturaForm.orarioChiusura}
                    onChange={(e) => setAperturaForm(prev => ({ ...prev, orarioChiusura: e.target.value }))}
                    className="input"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t dark:border-gray-700">
              <button onClick={() => setShowAperturaModal(false)} className="btn-secondary">Annulla</button>
              <button onClick={handleAddApertura} className="btn-primary">Aggiungi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
